import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Palette, Loader2, Check, Sun, Moon, Sparkles, SlidersHorizontal, Pipette } from 'lucide-react';
import { motion } from 'motion/react';
import { http } from '@/network/http';
import { useSettings } from '@/hooks/useSettings';
import toast from 'react-hot-toast';
import { GlassCard } from '@/components/glass';
import {
  staggerContainer,
  staggerItem,
} from '../utils/adminAnimations';
import {
  SECTION_IDS,
  SECTION_LABELS,
  SECTION_PROPS,
  PROP_LABELS,
  SCHEME_PRESETS,
  schemeKey,
  colorKey,
} from '../utils/colorSchemes';
import type { ColorScheme, SectionId } from '../utils/colorSchemes';

/* ─── Theme presets (moved from RegionConfigPage) ─── */

interface ThemePreset {
  id: string;
  label: string;
  labelHe: string;
  primaryColor: string;
  accentColor: string;
}

const THEME_PRESETS: ThemePreset[] = [
  { id: 'liquid-glass-il', label: 'Liquid Glass', labelHe: 'זכוכית נוזלית', primaryColor: '#007AFF', accentColor: '#28CD41' },
  { id: 'medical-blue', label: 'Medical Blue', labelHe: 'כחול רפואי', primaryColor: '#1565c0', accentColor: '#00897b' },
  { id: 'teal', label: 'Teal', labelHe: 'טורקיז', primaryColor: '#00897b', accentColor: '#1565c0' },
  { id: 'corporate', label: 'Corporate', labelHe: 'עסקי', primaryColor: '#37474f', accentColor: '#546e7a' },
];

const SCHEMES: { id: ColorScheme; icon: typeof Sun; labelEn: string; labelHe: string }[] = [
  { id: 'light', icon: Sun, labelEn: 'Light', labelHe: 'בהיר' },
  { id: 'dark', icon: Moon, labelEn: 'Dark', labelHe: 'כהה' },
  { id: 'glass', icon: Sparkles, labelEn: 'Glass', labelHe: 'זכוכית' },
  { id: 'custom', icon: SlidersHorizontal, labelEn: 'Custom', labelHe: 'מותאם' },
];

export function ColorManagerPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { data: settings, isLoading } = useSettings();
  const isHebrew = i18n.language === 'he';

  // Brand colors state (moved from RegionConfigPage)
  const [themePreset, setThemePreset] = useState('liquid-glass-il');
  const [primaryColor, setPrimaryColor] = useState('#007AFF');
  const [accentColor, setAccentColor] = useState('#28CD41');

  // Local state: scheme per section + custom colors
  const [schemes, setSchemes] = useState<Record<SectionId, ColorScheme>>({
    userNav: 'light',
    userBnav: 'light',
    userPage: 'light',
    adminNav: 'light',
    adminPage: 'light',
  });

  const [customColors, setCustomColors] = useState<Record<string, string>>({});

  // Sync from server settings
  useEffect(() => {
    if (!settings) return;

    // Brand colors
    if (settings.themePreset) setThemePreset(settings.themePreset);
    if (settings.primaryColor) setPrimaryColor(settings.primaryColor);
    if (settings.colorAccent) setAccentColor(settings.colorAccent);

    // Section schemes
    const newSchemes = { ...schemes };
    for (const section of SECTION_IDS) {
      const val = settings[schemeKey(section)];
      if (val && ['light', 'dark', 'glass', 'custom'].includes(val)) {
        newSchemes[section] = val as ColorScheme;
      }
    }
    setSchemes(newSchemes);

    const newCustom: Record<string, string> = {};
    for (const section of SECTION_IDS) {
      for (const prop of SECTION_PROPS[section]) {
        const key = colorKey(section, prop);
        if (settings[key]) {
          newCustom[key] = settings[key];
        }
      }
    }
    setCustomColors(newCustom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const saveSettings = useMutation({
    mutationFn: async (data: Record<string, string>) => {
      await http.put('/settings', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] });
      toast.success(t('settings.saved'));
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const handleSave = () => {
    const payload: Record<string, string> = {
      themePreset,
      primaryColor,
      colorAccent: accentColor,
    };
    for (const section of SECTION_IDS) {
      payload[schemeKey(section)] = schemes[section];
      // Always persist custom colors so switching to custom restores them
      for (const prop of SECTION_PROPS[section]) {
        const key = colorKey(section, prop);
        const fallback = (SCHEME_PRESETS.light[section] as unknown as Record<string, string>)[prop];
        payload[key] = customColors[key] || fallback;
      }
    }
    saveSettings.mutate(payload);
  };

  const selectPreset = (preset: ThemePreset) => {
    setThemePreset(preset.id);
    setPrimaryColor(preset.primaryColor);
    setAccentColor(preset.accentColor);
  };

  const setScheme = (section: SectionId, scheme: ColorScheme) => {
    setSchemes((prev) => ({ ...prev, [section]: scheme }));
  };

  const setCustomColor = (section: SectionId, prop: string, value: string) => {
    const key = colorKey(section, prop);
    setCustomColors((prev) => ({ ...prev, [key]: value }));
  };

  /** Get the resolved color for a section.prop (based on current local state) */
  const getResolvedColor = (section: SectionId, prop: string): string => {
    const scheme = schemes[section];
    if (scheme !== 'custom') {
      return (SCHEME_PRESETS[scheme][section] as unknown as Record<string, string>)[prop];
    }
    const key = colorKey(section, prop);
    return customColors[key] || (SCHEME_PRESETS.light[section] as unknown as Record<string, string>)[prop];
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="rounded-xl border border-gray-200 p-8 shadow-sm glass-surface">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6 pb-8"
    >
      {/* Header */}
      <motion.div variants={staggerItem} className="space-y-4">
        <div className="inline-flex items-center justify-center rounded-xl border border-gray-200 p-3 shadow-sm glass-surface">
          <Palette className="h-6 w-6 text-slate-800" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">
            {isHebrew ? 'צבעי ממשק' : 'UI Colors'}
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            {isHebrew
              ? 'הגדר ערכת צבעים לכל חלק בממשק בנפרד.'
              : 'Configure color schemes for each UI section independently.'}
          </p>
        </div>
      </motion.div>

      {/* Brand Colors — theme presets + primary/accent */}
      <motion.div variants={staggerItem}>
        <GlassCard
          title={isHebrew ? 'צבעי מותג' : 'Brand Colors'}
          icon={<Pipette className="h-4 w-4" />}
        >
          {/* Preset buttons */}
          <div className="flex flex-wrap gap-2 mb-5">
            {THEME_PRESETS.map((preset) => {
              const active = themePreset === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => selectPreset(preset)}
                  className="relative flex items-center gap-2.5 rounded-xl px-4 py-2.5 text-sm font-medium transition-all"
                  style={{
                    border: active ? `2px solid ${preset.primaryColor}` : '2px solid rgba(0,0,0,0.08)',
                    backgroundColor: active ? `${preset.primaryColor}10` : 'rgba(255,255,255,0.6)',
                    boxShadow: active ? `0 2px 12px ${preset.primaryColor}20` : '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                >
                  <div className="flex gap-1">
                    <div className="w-4 h-4 rounded-full border border-white/50" style={{ backgroundColor: preset.primaryColor }} />
                    <div className="w-4 h-4 rounded-full border border-white/50" style={{ backgroundColor: preset.accentColor }} />
                  </div>
                  {isHebrew ? preset.labelHe : preset.label}
                  {active && <Check size={14} className="text-primary-600" />}
                </button>
              );
            })}
          </div>

          {/* Color pickers */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-neutral-700">
                {isHebrew ? 'צבע ראשי' : 'Primary Color'}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-10 w-10 cursor-pointer rounded-lg border border-gray-200"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-mono shadow-sm focus:outline-none"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-neutral-700">
                {isHebrew ? 'צבע משני' : 'Accent Color'}
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="h-10 w-10 cursor-pointer rounded-lg border border-gray-200"
                />
                <input
                  type="text"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-mono shadow-sm focus:outline-none"
                />
              </div>
            </div>
          </div>
        </GlassCard>
      </motion.div>

      {/* Section cards */}
      {SECTION_IDS.map((section) => (
        <motion.div key={section} variants={staggerItem}>
          <GlassCard
            title={isHebrew ? SECTION_LABELS[section].he : SECTION_LABELS[section].en}
            icon={<Palette className="h-4 w-4" />}
          >
            {/* Scheme selector */}
            <div className="flex flex-wrap gap-2 mb-4">
              {SCHEMES.map((s) => {
                const isSelected = schemes[section] === s.id;
                const Icon = s.icon;
                return (
                  <button
                    key={s.id}
                    onClick={() => setScheme(section, s.id)}
                    className="relative flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all"
                    style={{
                      border: isSelected
                        ? '2px solid var(--color-primary-500)'
                        : '2px solid rgba(0,0,0,0.08)',
                      backgroundColor: isSelected
                        ? 'var(--color-primary-50)'
                        : 'rgba(255,255,255,0.6)',
                      color: isSelected
                        ? 'var(--color-primary-700)'
                        : 'var(--color-label-secondary)',
                    }}
                  >
                    <Icon size={16} />
                    {isHebrew ? s.labelHe : s.labelEn}
                    {isSelected && (
                      <Check size={14} className="text-primary-600" />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Color preview swatches */}
            <div className="flex flex-wrap gap-2 mb-4">
              {SECTION_PROPS[section].map((prop) => {
                const color = getResolvedColor(section, prop);
                const label = isHebrew ? PROP_LABELS[prop]?.he : PROP_LABELS[prop]?.en;
                return (
                  <div key={prop} className="flex items-center gap-1.5">
                    <div
                      className="w-6 h-6 rounded-lg border border-gray-300"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-xs text-neutral-500">{label || prop}</span>
                  </div>
                );
              })}
            </div>

            {/* Custom color pickers (only when scheme=custom) */}
            {schemes[section] === 'custom' && (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-t border-gray-200 pt-4">
                {SECTION_PROPS[section].map((prop) => {
                  const key = colorKey(section, prop);
                  const value = customColors[key] || (SCHEME_PRESETS.light[section] as unknown as Record<string, string>)[prop];
                  const label = isHebrew ? PROP_LABELS[prop]?.he : PROP_LABELS[prop]?.en;
                  return (
                    <div key={prop}>
                      <label className="mb-1 block text-xs font-semibold text-neutral-600">
                        {label || prop}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={toHex(value)}
                          onChange={(e) => setCustomColor(section, prop, e.target.value)}
                          className="h-8 w-8 cursor-pointer rounded-lg border border-gray-200"
                        />
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => setCustomColor(section, prop, e.target.value)}
                          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-mono shadow-sm focus:outline-none"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </GlassCard>
        </motion.div>
      ))}

      {/* Save */}
      <motion.div variants={staggerItem} className="flex justify-end pt-2">
        <button
          onClick={handleSave}
          disabled={saveSettings.isPending}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-primary-600 px-6 py-3 text-sm font-bold text-white shadow-sm transition-all hover:bg-primary-700 active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saveSettings.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {t('common.save')}
        </button>
      </motion.div>
    </motion.div>
  );
}

/** Convert rgba/named colors to hex for <input type="color"> (best effort) */
function toHex(color: string): string {
  if (color.startsWith('#') && (color.length === 7 || color.length === 4)) return color;
  // For rgba/non-hex, return a fallback — the text input still shows the real value
  return '#000000';
}
